import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import type { ResolvedEdit, ScoreBreakdown, SourcePack } from "@desk/core";
import {
  EMOTION_AXES,
  EMOTION_TUNING,
  baseEmotionVector,
  editEmotionVector,
  toAxisValues,
  crowdReaction,
  crowdFit,
  EMOTION_AXIS_COLORS,
  type CaptionPlacementZone,
  type CrowdEmotionProfile,
  type CrowdReaction,
  type EmotionAxis,
  type EmotionVector,
} from "../../game/emotion-model.js";
import { landingConfigForPack } from "../../content/landing/index.js";
import { hintsForPack } from "../../content/levels/index.js";
import { crowdChromeForPack } from "../../content/landing/crowds-presentation.js";
import { changeRows } from "../lib/caption-impacts.js";
import { ImpactChips } from "./ImpactChips.js";
import { radarPoints, radarSmoothPath } from "../lib/radar-geometry.js";

/**
 * "How it's landing": crowd-selectable emotion overlay (PRIMARY), reachable
 * from the editor LVL pill. Pick any of the three Vet Day crowds and see the
 * live reel vector overlaid on THAT crowd's green (wants) + red (backlash)
 * zones. Below it, a secondary "WHAT THE EDIT IS DOING" read: labeled signal
 * bars (Hook, Pacing, ...) plus a warming/hot/cooling status line.
 *
 * Presentation only: the emotion overlay reads the emotion model and the signal
 * bars are read-only projections of the existing ScoreBreakdown. This panel
 * never mutates or re-derives `@desk/core` scoring.
 */
export interface StatsPanelProps {
  open: boolean;
  onClose: () => void;
  /** Retained for editor wiring; the crowd panel no longer reads scoring. */
  pack?: SourcePack;
  score?: ScoreBreakdown;
  /** Current resolved edit — read-only source for the live emotion vector. */
  resolved?: ResolvedEdit | null;
  /** Visible jump-cuts in the edited timeline (raise "looks edited" Doubt). */
  jumpCuts?: number;
  /** True when Seamless Transitions is owned (softens jump-cut Doubt). */
  seamlessTransitions?: boolean;
  /** Beats trimmed to their clean end (Precision Trim) — clears their leak Doubt. */
  precisionTrimmedBeatIds?: readonly string[];
  /** Active stackable effect ids (rain / lightning / focus blur) for the landing. */
  activeEffectIds?: readonly string[];
  /** The caption's current PLACEMENT zone (chyron/subject/top) — a light lever. */
  captionPlacement?: CaptionPlacementZone;
  /**
   * The crowd this edit is cutting for (the audience objective). Initializes the
   * selected crowd so "How it's landing" opens on the CHOSEN target rather than
   * always the served crowd. Falls back to the served crowd when absent.
   */
  chosenCrowdId?: string;
  comboLabel?: string | null;
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Whether the compact live spider is pinned beside the preview (persisted). */
  spiderPinned?: boolean;
  /** Toggle the pinned live spider on/off — the pin control lives beside the radar. */
  onTogglePin?: () => void;
  /** True when Precision Trim is owned; gates the leak fix action's route. */
  precisionTrim?: boolean;
  /** Toggle a beat's Precision Trim (session state); wired when owned. */
  onToggleTrim?: (beatId: string) => void;
  /** Open the Studio store at Precision Trim (used when the tool is not owned). */
  onOpenTrimStore?: () => void;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const milliToUnit = (m: number) => clamp01(m / 1000);
const pct = (u: number) => `${Math.round(u * 100)}%`;
/** kebab slug for a stat label, used for stable tooltip test ids / aria ids. */
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** Tone ramp for the signal bars: cold → warming → hot. */
function toneColor(u: number): string {
  if (u >= 0.66) return "#33e6be";
  if (u >= 0.33) return "#ffc857";
  return "#ff6a4d";
}

/**
 * Overall "how it's landing" read for the crowd we're cutting for (served).
 * A single warming / hot / cooling word + one sentence. Presentation only.
 */
function landingStatus(served: CrowdReaction): { word: string; tone: string; line: string } {
  if (served.inRed) {
    return {
      word: "cooling",
      tone: "#ff6a4d",
      line: "Your own crowd is tipping toward their backlash zone. Ease off the red.",
    };
  }
  if (served.fit >= 0.5) {
    return {
      word: "hot",
      tone: "#33e6be",
      line: "You're deep in your crowd's green. This edit is landing.",
    };
  }
  if (served.fit > 0.15) {
    return {
      word: "warming",
      tone: "#ffc857",
      line: "Signal is building for your crowd. Keep filling their green to heat it up.",
    };
  }
  return {
    word: "cooling",
    tone: "#8b8b97",
    line: "Barely moving your crowd yet. Start hitting what they want.",
  };
}

/**
 * Render a plain string with any emotion-axis word (Rage, Alarm, Doubt, Funny,
 * Pity, Warm) recoloured to match the spider, so inline mentions read in the
 * same colour as the plot. Case-insensitive; the axis word keeps its original
 * casing. Presentation only.
 */
const AXIS_WORD_RE = new RegExp(`\\b(${EMOTION_AXES.join("|")})\\b`, "gi");
function renderEmotionText(text: string): ReactNode {
  const parts = text.split(AXIS_WORD_RE);
  return parts.map((part, i) => {
    const axis = EMOTION_AXES.find((a) => a.toLowerCase() === part.toLowerCase());
    if (!axis) return part;
    return (
      <span key={i} className="stats-axis-word" style={{ color: EMOTION_AXIS_COLORS[axis] }}>
        {part}
      </span>
    );
  });
}

// Radar geometry (points + Catmull-Rom smoothing) is shared with the Brief
// crowd mini-spiders via lib/radar-geometry so the two can't drift apart.

/**
 * The shared emotion spider SVG: a crowd's GREEN wants + RED backlash zones with
 * the live reel vector overlaid. Reused by the full "How it's landing" panel and
 * by the compact pinned spider beside the editor preview. Presentation only —
 * reads the emotion model, never scoring. `compact` hides axis labels and shrinks
 * strokes so it stays legible at a small size in the preview corner.
 */
export function EmotionSpider({
  profile,
  vector,
  showVector,
  compact = false,
}: {
  profile: CrowdEmotionProfile;
  vector: EmotionVector;
  showVector: boolean;
  compact?: boolean;
}) {
  // Render-only floor so a sparse profile still reads as a shaded ZONE. Zones and
  // the live reel are drawn as smoothed (spline) blobs so their corners are soft.
  const zonePath = (v: EmotionVector) =>
    radarSmoothPath(toAxisValues(v).map((n) => Math.max(n, 0.09)));
  const greenPath = zonePath(profile.green);
  const redPath = zonePath(profile.red);
  const editPath = radarSmoothPath(toAxisValues(vector));
  return (
    <svg viewBox="0 0 104 108" aria-hidden>
      <polygon
        points={radarPoints(EMOTION_AXES.map(() => 1))}
        fill="none"
        stroke="rgba(255,255,255,.1)"
        strokeWidth="1"
      />
      <polygon
        points={radarPoints(EMOTION_AXES.map(() => 0.5))}
        fill="none"
        stroke="rgba(255,255,255,.06)"
        strokeWidth="1"
      />
      {/* RED backlash zone (swaps with the selected crowd) — smoothed spline */}
      <path
        d={redPath}
        fill="rgba(255,61,127,.18)"
        stroke="#ff3d7f"
        strokeWidth="1.3"
        strokeDasharray="3 2"
        strokeLinejoin="round"
        data-testid="emotion-red-zone"
      />
      {/* GREEN wants zone (swaps with the selected crowd) — smoothed spline */}
      <path
        d={greenPath}
        fill="rgba(51,230,190,.18)"
        stroke="#33e6be"
        strokeWidth="1.5"
        strokeLinejoin="round"
        data-testid="emotion-green-zone"
      />
      {/* Live reel vector — smoothed spline */}
      {showVector ? (
        <path
          d={editPath}
          fill="rgba(255,255,255,.14)"
          stroke="#ffffff"
          strokeWidth="1.4"
          strokeLinejoin="round"
          data-testid="emotion-live-vector"
        />
      ) : null}
      {compact
        ? null
        : EMOTION_AXES.map((axis, i) => {
            const angle = (-90 + (360 / EMOTION_AXES.length) * i) * (Math.PI / 180);
            const x = 52 + 46 * Math.cos(angle);
            const y = 50 + 46 * Math.sin(angle);
            return (
              <text
                key={axis}
                x={x.toFixed(1)}
                y={(y + 2).toFixed(1)}
                fontSize="6"
                fontWeight="600"
                fill={EMOTION_AXIS_COLORS[axis]}
                textAnchor="middle"
              >
                {axis}
              </text>
            );
          })}
    </svg>
  );
}

/** A crowd tab: id + glyph + accent, derived per-pack from the crowd chrome. */
interface CrowdTab {
  id: string;
  icon: string;
  accent: string;
  /** The same one-line "what this crowd wants" blurb the Brief card shows. */
  wants: string;
}

/** Join axis words truthfully: "rage", "rage and alarm", "rage, alarm and pity". */
function listWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** Red axes the reel has genuinely spilled into for this crowd. */
function spilledRedAxes(vector: EmotionVector, profile: CrowdEmotionProfile): EmotionAxis[] {
  return EMOTION_AXES.filter(
    (a) => profile.red[a] > 0 && clamp01(vector[a]) >= EMOTION_TUNING.RED_TRIGGER_FRAC * profile.red[a],
  );
}

/** Green axes the crowd wants that the reel has NOT yet hit. */
function missingGreenAxes(vector: EmotionVector, profile: CrowdEmotionProfile): EmotionAxis[] {
  return EMOTION_AXES.filter(
    (a) => profile.green[a] > 0 && clamp01(vector[a]) < EMOTION_TUNING.GREEN_HIT_FRAC * profile.green[a],
  );
}

/**
 * What to say about a crowd you are NOT cutting for.
 *
 * The old panel offered "Ask a colleague" for every crowd, which handed the
 * player a recipe for winning a crowd they had explicitly not chosen — three
 * simultaneous objectives instead of one, and no reason to ever pick. A
 * bystander gets an honest read of how they are taking the cut and nothing
 * actionable; wanting their approval is a reason to go back to the Brief and
 * cut for them instead.
 */
export function bystanderRead(reaction: CrowdReaction, hasSignal: boolean): string {
  if (!hasSignal) return "Nothing to react to yet.";
  const { wantsHit, wantsTotal, fit, backlash, inRed } = reaction;
  const wants = `${wantsHit}/${wantsTotal} of what they want`;
  if (inRed) {
    return `They turn on this one. That is the price of the cut you picked.`;
  }
  // How much they LIKE it leads, because that is the surprising part; the edge
  // of discomfort is a qualifier, not the headline. Getting this order wrong
  // reads as a contradiction when a bystander is both fully served and edgy.
  const loved = wantsTotal > 0 && wantsHit >= wantsTotal;
  const edgy = backlash >= 0.34;
  if (loved) {
    return edgy
      ? `They are into it anyway, ${wants}, though it edges their red.`
      : `They would enjoy this too, ${wants}. Not the job, but no harm in it.`;
  }
  if (edgy) return "Suspicious of it, not angry enough to pile on.";
  if (fit >= 0.5 || wantsHit > 0) {
    return `Mildly into it, ${wants}. It is not their story, so they scroll on.`;
  }
  return "This is not for them, and they can tell. No harm done.";
}

/**
 * Truthful, live "why" read for the crowd you ARE cutting for. Coaching lives
 * here and only here; every other crowd goes through `bystanderRead`.
 */
function whyLine(
  vector: EmotionVector,
  profile: CrowdEmotionProfile,
  reaction: CrowdReaction,
  hasSignal: boolean,
): string {
  if (!hasSignal) {
    return "Your reel is still blank. Start cutting to plot it against this crowd.";
  }
  const wants = `${reaction.wantsHit}/${reaction.wantsTotal} wants hit`;
  // Backlash language ONLY when the vector actually reaches their red.
  if (reaction.inRed) {
    const axes = spilledRedAxes(vector, profile).map((a) => a.toLowerCase());
    const names = listWords(axes.length ? axes : ["reel"]);
    return `Your ${names} is tipping into your own crowd's backlash zone, watch it.`;
  }
  if (reaction.fit >= 0.5) return `In the green: ${wants}.`;
  const missing = missingGreenAxes(vector, profile).map((a) => a.toLowerCase());
  return reaction.wantsHit > 0
    ? `Warming up: ${wants}. Lean into ${listWords(missing)} to fill their green.`
    : `Not landing yet: ${wants}. This crowd wants ${listWords(missing)}.`;
}

export function StatsPanel({
  open,
  onClose,
  pack,
  score,
  resolved,
  jumpCuts = 0,
  seamlessTransitions = false,
  precisionTrimmedBeatIds,
  activeEffectIds,
  captionPlacement,
  chosenCrowdId,
  returnFocusRef,
  spiderPinned = false,
  onTogglePin,
  precisionTrim = false,
  onToggleTrim,
  onOpenTrimStore,
}: StatsPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  // The current scenario's landing config (crowds + tunable levers), driven by
  // the pack id. Presentation only; never feeds scoring.
  const landingConfig = useMemo(() => landingConfigForPack(pack?.id), [pack?.id]);
  // Crowd tabs in Brief order (served crowd in the middle), driven by the pack's
  // chrome so a scenario with its own crowds shows THEM. Presentation only.
  const crowdTabs = useMemo<CrowdTab[]>(
    () =>
      crowdChromeForPack(pack?.id)
        .filter((c) => landingConfig.crowds[c.id])
        .map((c) => ({ id: c.id, icon: c.icon, accent: c.accent, wants: c.wants })),
    [pack?.id, landingConfig],
  );

  // The crowd this edit is FOR. `chosenCrowdId` is what the objective is scored
  // against, so it — not the scenario's default served crowd — decides who the
  // panel coaches and who it merely reports on. They are the same until the
  // player picks a different crowd in the Brief.
  const targetId =
    chosenCrowdId && landingConfig.crowds[chosenCrowdId] ? chosenCrowdId : landingConfig.servedCrowdId;
  // Open on the CHOSEN crowd (the edit's audience objective) when it exists in
  // this scenario, else the served crowd. A pack switch is reconciled by the
  // effect below. Presentation only.
  const [selectedId, setSelectedId] = useState<string>(
    (chosenCrowdId && landingConfig.crowds[chosenCrowdId] ? chosenCrowdId : null) ??
      landingConfig.servedCrowdId,
  );
  // Which stat row's (i) "how to raise it" popover is open, plus the viewport
  // coords to anchor it. The tip renders with position: fixed so it can overflow
  // the panel / phone frame without being clipped. Tap-to-toggle on touch;
  // hover/focus also reveal it. Presentation only.
  const [tip, setTip] = useState<{ label: string; left: number; top: number } | null>(null);
  const openTipFrom = (label: string, el: HTMLElement | null) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    // The overlay carries a backdrop-filter, which makes it the containing block
    // for our position: fixed tip. Anchor to the overlay's coordinate system so
    // the tip lands over the (i) while still escaping the sheet's overflow clip.
    const cb = el.closest(".stats-panel-overlay")?.getBoundingClientRect();
    const ox = cb?.left ?? 0;
    const oy = cb?.top ?? 0;
    setTip({ label, left: r.left - ox + r.width / 2, top: r.top - oy });
  };
  const closeTip = (label: string) =>
    setTip((cur) => (cur?.label === label ? null : cur));

  // ── Live reel vector + per-crowd reactions (presentation only) ──
  // Start from the pack's raw-footage baseline so YOUR REEL always shows a small
  // non-zero shape; edits grow it from there. Never feeds scoring.
  const baseVector = useMemo(() => baseEmotionVector(landingConfig), [landingConfig]);
  const editVector = useMemo(
    () =>
      editEmotionVector(resolved, landingConfig, {
        base: baseVector,
        jumpCuts,
        seamlessTransitions,
        tickRate: pack?.tickRate,
        precisionTrimmedBeatIds,
        activeEffectIds,
        captionPlacement,
      }),
    [resolved, landingConfig, baseVector, jumpCuts, seamlessTransitions, pack?.tickRate, precisionTrimmedBeatIds, activeEffectIds, captionPlacement],
  );
  const vectorHasSignal = toAxisValues(editVector).some((v) => v > 0.02);

  // "What you changed": every applied edit + its emotion impact, from the SAME
  // levers the landing folds in. Shown in the Advanced read. Presentation only.
  const changes = useMemo(
    () => changeRows(resolved, landingConfig, { activeEffectIds, captionPlacement, jumpCuts }),
    [resolved, landingConfig, activeEffectIds, captionPlacement, jumpCuts],
  );

  // ── Secondary "WHAT THE EDIT IS DOING" signals, read-only ScoreBreakdown ──
  // Each row carries a one-line "how to raise it" tip surfaced via an accessible
  // (i) popover. Presentation only — the tips never touch scoring.
  // "Audience fit" is DERIVED from the served crowd's live emotion-model crowd
  // fit (how much of their green "wants" zone the reel fills), so it agrees with
  // the wants-hit read instead of contradicting it. Presentation only.
  const targetFit = useMemo(
    () => crowdFit(editVector, landingConfig.crowds[targetId]!),
    [editVector, landingConfig, targetId],
  );
  const signals = useMemo(() => {
    const c = score?.components;
    if (!c) return [];
    return [
      { label: "Hook strength", u: milliToUnit(c.hookMilli), tip: "Open on your strongest beat." },
      { label: "Pacing", u: milliToUnit(c.pacingMilli), tip: "Cut the dead time; keep it moving." },
      { label: "Emotional punch", u: milliToUnit(c.intensityMilli), tip: "Add a loaded caption or a tense sound." },
      { label: "Audio sync", u: milliToUnit(c.audioSyncMilli), tip: "Match the sound mood to your cut." },
      { label: "Audience fit", u: clamp01(targetFit), tip: "Serve your crowd's wants (their green)." },
      { label: "Freshness", u: milliToUnit(c.noveltyMilli), tip: "Avoid the overused framings." },
      { label: "Production", u: milliToUnit(c.productionMilli), tip: "Add a look or motion for polish." },
    ];
  }, [score, targetFit]);

  const reactions = useMemo(() => {
    const out: Record<string, CrowdReaction> = {};
    for (const tab of crowdTabs) {
      const profile = landingConfig.crowds[tab.id]!;
      out[tab.id] = crowdReaction(editVector, profile, tab.id === targetId);
    }
    return out;
  }, [editVector, landingConfig, crowdTabs, targetId]);

  // A pack switch can leave `selectedId` pointing at a crowd that does not exist
  // in the new scenario; fall back to the target crowd until the reset effect runs.
  const activeId = landingConfig.crowds[selectedId] ? selectedId : targetId;
  useEffect(() => {
    if (!landingConfig.crowds[selectedId]) setSelectedId(targetId);
  }, [landingConfig, selectedId, targetId]);
  const selectedTab = crowdTabs.find((t) => t.id === activeId);
  const selectedProfile = landingConfig.crowds[activeId]!;
  const selectedReaction = reactions[activeId]!;
  const selectedIsTarget = activeId === targetId;

  // Overall landing read is anchored to the crowd we're cutting for, so the
  // status word stays stable as you flip between crowd tabs.
  const targetReaction = reactions[targetId]!;
  const status = landingStatus(targetReaction);

  // ONE read per crowd, in one place. Coaching for the crowd you are cutting
  // for; a bystander report for anyone else. Two stacked boxes said the same
  // thing twice and could contradict each other.
  const why = selectedIsTarget
    ? whyLine(editVector, selectedProfile, selectedReaction, vectorHasSignal)
    : bystanderRead(selectedReaction, vectorHasSignal);

  // ── "Ask a colleague": data-driven, opt-in nudge for the SELECTED crowd ──
  // Surfaces the LEVEL-AUTHORED hint(s) for that crowd's currently UNMET green
  // wants (the gaps between the reel and their zone). All hint COPY lives in
  // level.json; code only picks which gap(s) to show. Presentation only.
  const levelHints = useMemo(() => hintsForPack(pack?.id), [pack?.id]);
  const hasHints = Object.keys(levelHints).length > 0;
  const [askOpen, setAskOpen] = useState(false);
  const colleagueHints = useMemo(() => {
    const perCrowd = levelHints[activeId] ?? {};
    return missingGreenAxes(editVector, selectedProfile)
      .map((axis) => ({ axis, text: perCrowd[axis] }))
      .filter((h): h is { axis: EmotionAxis; text: string } => Boolean(h.text));
  }, [levelHints, activeId, editVector, selectedProfile]);

  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      closeRef.current?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      returnFocusRef?.current?.focus?.();
    }
  }, [open, returnFocusRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="stats-panel-overlay"
      data-testid="stats-panel"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="stats-panel-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stats-panel-title"
      >
        <header className="stats-panel-head">
          <h2 id="stats-panel-title">How it's landing</h2>
          <button
            type="button"
            ref={closeRef}
            className="stats-panel-close"
            data-testid="close-stats-panel"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <p className="stats-lede">Pick a crowd to see why your reel lands with them, or turns them.</p>

        {/* ── Crowd selector: Brief order, served crowd emphasized ── */}
        <div className="stats-crowd-tabs" role="tablist" aria-label="Crowds" data-testid="crowd-tabs">
          {crowdTabs.map((tab) => {
            const profile = landingConfig.crowds[tab.id]!;
            const r = reactions[tab.id]!;
            const isTarget = tab.id === targetId;
            const active = tab.id === activeId;
            const dotColor = r.inRed ? "#ff6a4d" : isTarget && r.fit >= 0.5 ? "#33e6be" : "#8b8b97";
            return (
              <button
                type="button"
                role="tab"
                key={tab.id}
                aria-selected={active}
                className={`stats-crowd-tab${active ? " is-active" : ""}${isTarget ? " is-served" : ""}${r.inRed ? " is-red" : ""}`}
                data-testid={`crowd-tab-${tab.id}`}
                data-in-red={r.inRed ? "1" : "0"}
                data-target={isTarget ? "1" : "0"}
                style={{ ["--crowd-accent" as string]: tab.accent }}
                onClick={() => setSelectedId(tab.id)}
              >
                {isTarget ? (
                  <span className="stats-crowd-tab-badge" aria-hidden>
                    ◆ EDITING FOR
                  </span>
                ) : null}
                <span className="stats-crowd-tab-face" aria-hidden>
                  {tab.icon}
                </span>
                <span className="stats-crowd-tab-name">{profile.label}</span>
                <span className="stats-crowd-tab-state">
                  <span className="stats-crowd-tab-dot" style={{ background: dotColor }} aria-hidden />
                  {vectorHasSignal ? (r.inRed ? "backlash" : isTarget ? r.status : "fine") : "waiting"}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Selected crowd overlay: green + red zones + live reel vector ── */}
        <div className="stats-emotion" data-testid="emotion-live">
          <div className="stats-emotion-radar">
            <EmotionSpider
              profile={selectedProfile}
              vector={editVector}
              showVector={vectorHasSignal}
            />
            {onTogglePin ? (
              <button
                type="button"
                className={`stats-pin-spider${spiderPinned ? " is-pinned" : ""}`}
                data-testid="pin-spider-toggle"
                data-pinned={spiderPinned ? "1" : "0"}
                aria-pressed={spiderPinned}
                aria-label={
                  spiderPinned
                    ? "Unpin the live spider from the preview"
                    : "Pin this live spider beside the preview"
                }
                title={spiderPinned ? "Unpin live spider" : "Pin live spider to editor"}
                onClick={onTogglePin}
              >
                <span aria-hidden>📌</span>
                <span className="stats-pin-spider-label">
                  {spiderPinned ? "Pinned" : "Pin to editor"}
                </span>
              </button>
            ) : null}
          </div>

          <div className="stats-emotion-legend" data-testid="emotion-legend">
            <span className="stats-legend-item">
              <span className="stats-legend-dot stats-legend-green" /> wants
            </span>
            <span className="stats-legend-item">
              <span className="stats-legend-dot stats-legend-red" /> backlash
            </span>
            {vectorHasSignal ? (
              <span
                className="stats-legend-item"
                title="Your reel starts from what the raw footage already offers. Your edits grow it."
              >
                <span className="stats-legend-dot stats-legend-reel" /> your reel
              </span>
            ) : null}
          </div>

          {/* The crowd's own one-line want, verbatim from the Brief card, so the
              spider above it has a caption in plain language rather than a
              generic note about baselines. */}
          <p className="stats-crowd-wants" data-testid="emotion-crowd-wants">
            {selectedTab?.wants ?? ""}
          </p>


          {/* The single read for the selected crowd. Tagged NOT YOUR CROWD, and
              styled down, when it is a bystander report rather than coaching. */}
          <p
            className={`stats-emotion-read${selectedReaction.inRed ? " is-red" : ""}${
              selectedIsTarget ? "" : " is-bystander"
            }`}
            data-testid="emotion-why-read"
            data-in-red={selectedReaction.inRed ? "1" : "0"}
            data-bystander={selectedIsTarget ? "0" : "1"}
          >
            {selectedIsTarget ? null : (
              <span className="stats-bystander-tag" data-testid="stats-bystander">
                NOT YOUR CROWD
              </span>
            )}
            {renderEmotionText(why)}
          </p>

          {/* ── Ask a colleague: opt-in, level-authored nudge for the gaps.
                 Only for the crowd you are cutting for — a recipe for winning a
                 crowd you did not choose removes the reason to choose. ── */}
          {selectedIsTarget && hasHints ? (
            <div className="stats-colleague" data-testid="stats-colleague">
              <button
                type="button"
                className={`stats-colleague-btn${askOpen ? " is-open" : ""}`}
                data-testid="ask-colleague"
                aria-expanded={askOpen}
                onClick={() => setAskOpen((o) => !o)}
              >
                <span aria-hidden>💬</span> Ask a colleague
              </button>
              {askOpen ? (
                <div className="stats-colleague-hints" data-testid="colleague-hints">
                  {colleagueHints.length > 0 ? (
                    <>
                      <p className="stats-colleague-lede">
                        To land <strong>{selectedProfile.label}</strong>, they still want:
                      </p>
                      <ul>
                        {colleagueHints.map((h) => (
                          <li key={h.axis} data-axis={h.axis}>
                            <span
                              className="stats-colleague-axis"
                              style={{ color: EMOTION_AXIS_COLORS[h.axis] }}
                            >
                              {h.axis}
                            </span>
                            <span>{renderEmotionText(h.text)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="stats-colleague-clear" data-testid="colleague-clear">
                      You're already filling <strong>{selectedProfile.label}</strong>'s green. Ship it.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ── Advanced (info-only): "What you changed" + the craft read ── */}
        <details className="stats-advanced" data-testid="stats-advanced">
            <summary className="stats-advanced-summary">
              <span className="stats-advanced-title">Advanced</span>
              <span className="stats-advanced-sub">what you changed · under the hood</span>
            </summary>
          <div className="stats-changes" data-testid="stats-changes-list">
            <div className="stats-signals-head">WHAT YOU CHANGED</div>
            {changes.length === 0 ? (
              <p className="stats-changes-empty">No edits yet. The reel is raw.</p>
            ) : (
              <ul className="stats-changes-rows">
                {changes.map((c) => (
                  <li className="stats-changes-row" key={c.key}>
                    <span className="stats-changes-label">{c.label}</span>
                    <ImpactChips impacts={c.impacts} minimalLabel="cosmetic" />
                  </li>
                ))}
              </ul>
            )}
          </div>
          {signals.length > 0 ? (
          <div className="stats-signals" data-testid="stats-signals">
            <div className="stats-signals-head">WHAT THE EDIT IS DOING</div>
            <p className="stats-doing-status" data-testid="stats-doing-status">
              <span className="stats-doing-word" style={{ color: status.tone }}>
                {status.word}
              </span>{" "}
              {status.line}
            </p>
            {signals.map((s) => {
              const sl = slug(s.label);
              const tipOpen = tip?.label === s.label;
              const tipId = `stat-tip-${sl}`;
              return (
              <div className="stats-signal-row" key={s.label}>
                <span className="stats-signal-label">
                  <span className="stats-signal-label-text">{s.label}</span>
                  <span className={`stats-info-wrap${tipOpen ? " is-open" : ""}`}>
                    <button
                      type="button"
                      className="stats-info-btn"
                      data-testid={`stat-info-${sl}`}
                      aria-label={`How to raise ${s.label}`}
                      aria-expanded={tipOpen}
                      aria-describedby={tipId}
                      onClick={(e) =>
                        tipOpen ? setTip(null) : openTipFrom(s.label, e.currentTarget)
                      }
                      onMouseEnter={(e) => openTipFrom(s.label, e.currentTarget)}
                      onMouseLeave={() => closeTip(s.label)}
                      onFocus={(e) => openTipFrom(s.label, e.currentTarget)}
                      onBlur={() => closeTip(s.label)}
                    >
                      i
                    </button>
                    <span
                      className="stats-info-tip"
                      role="tooltip"
                      id={tipId}
                      data-testid={`stat-tip-${sl}`}
                      style={
                        tipOpen ? { left: tip!.left, top: tip!.top } : undefined
                      }
                    >
                      {s.tip}
                    </span>
                  </span>
                </span>
                <div className="stats-signal-track">
                  <div
                    className="stats-signal-fill"
                    style={{ width: pct(s.u), background: toneColor(s.u) }}
                  />
                </div>
                <span className="stats-signal-val" style={{ color: toneColor(s.u) }}>
                  {pct(s.u)}
                </span>
              </div>
              );
            })}
          </div>
          ) : null}
          </details>
      </div>
    </div>
  );
}
